/**
 * Serviço de Criptografia para Op-Log
 * AIDEV-NOTE: Usa WebCrypto API com AES-GCM
 * Cada payload é criptografado com chave derivada do usuário
 */

export interface EncryptedPayload {
  iv: string; // Base64
  data: string; // Base64
  tag: string; // Base64
}

interface KeyMaterial {
  key: CryptoKey;
  salt: string;
}

export class CryptoService {
  private keyMaterial: KeyMaterial | null = null;

  /**
   * Inicializa com chave direta (para testes)
   */
  async initialize(keyBytes: Uint8Array): Promise<void> {
    if (keyBytes.length !== 32) {
      throw new Error('Chave deve ter 32 bytes para AES-GCM 256');
    }

    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes.buffer as ArrayBuffer,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    this.keyMaterial = { key, salt: 'test-salt' };
  }

  /**
   * Deriva chave de criptografia a partir de um segredo do usuário
   * AIDEV-SECURITY: Chave nunca é persistida, apenas em memória
   */
  async deriveKey(userSecret: string): Promise<void> {
    const encoder = new TextEncoder();

    // Gera salt aleatório
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const saltBase64 = this.uint8ArrayToBase64(salt);

    // Importa o segredo como KeyMaterial
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(userSecret),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    // Deriva chave AES-GCM usando PBKDF2
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(userSecret + saltBase64),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    this.keyMaterial = { key, salt: saltBase64 };
  }

  /**
   * Criptografa payload
   */
  async encrypt(data: Record<string, unknown>): Promise<EncryptedPayload> {
    if (!this.keyMaterial) {
      throw new Error('Chave de criptografia não inicializada');
    }

    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96 bits para GCM

    const jsonData = JSON.stringify(data);
    const encodedData = encoder.encode(jsonData);

    // Criptografa
    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      this.keyMaterial.key,
      encodedData
    );

    // Extrai tag de autenticação (últimos 16 bytes)
    const encryptedData = new Uint8Array(encrypted);
    const tag = encryptedData.slice(-16);
    const ciphertext = encryptedData.slice(0, -16);

    return {
      iv: this.uint8ArrayToBase64(iv),
      data: this.uint8ArrayToBase64(ciphertext),
      tag: this.uint8ArrayToBase64(tag)
    };
  }

  /**
   * Descriptografa payload
   */
  async decrypt(encrypted: EncryptedPayload): Promise<Record<string, unknown>> {
    if (!this.keyMaterial) {
      throw new Error('Chave de criptografia não inicializada');
    }

    const iv = this.base64ToUint8Array(encrypted.iv);
    const ciphertext = this.base64ToUint8Array(encrypted.data);
    const tag = this.base64ToUint8Array(encrypted.tag);

    // Combina ciphertext + tag
    const encryptedData = new Uint8Array(ciphertext.length + tag.length);
    encryptedData.set(ciphertext, 0);
    encryptedData.set(tag, ciphertext.length);

    // Descriptografa
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv.buffer
      } as AesGcmParams,
      this.keyMaterial.key,
      encryptedData
    );

    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decrypted);

    return JSON.parse(jsonString) as Record<string, unknown>;
  }

  /**
   * Limpa chave da memória
   */
  clearKey(): void {
    this.keyMaterial = null;
  }

  /**
   * Helpers para conversão Base64
   */
  uint8ArrayToBase64(buffer: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < buffer.length; i++) {
      binary += String.fromCharCode(buffer[i]!);
    }
    return btoa(binary);
  }

  base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}

export const cryptoService = new CryptoService();
